import { useCallback, useEffect, useRef, useState } from 'react';
import { downloadFile } from 'medere-ui-framework';
import { AsyncExportJobDTO } from '../dto/AsyncExportDTO';
import { downloadAsyncExport, getAsyncExportStatus } from '../services/AsyncExportController';
import { parseApiError } from '../services/apiError';
import useNotify from './useNotify';

/** Cada cuánto se consulta el estado mientras el archivo se genera. */
const POLL_INTERVAL_MS = 2000;

function percentOf(job: AsyncExportJobDTO): number {
    if (!job.totalRows) return 0;
    // 100 % recién cuando está listo para descargar
    return Math.min(99, Math.floor((job.processedRows * 100) / job.totalRows));
}

/**
 * Export en segundo plano: arranca el archivo en el backend, consulta su avance cada 2 s y lo descarga solo cuando
 * está listo, así un export grande no depende del timeout del request. Al salir de la pantalla deja de consultar
 * (el backend borra el archivo a los 30 minutos).
 *
 * `run(start, fallbackError)`: `start` es el pedido al endpoint de export del reporte.
 */
export default function useAsyncExport() {
    const notify = useNotify();
    const [exporting, setExporting] = useState(false);
    /** Avance en %, mientras se genera. */
    const [progress, setProgress] = useState<number | null>(null);
    const mounted = useRef(true);
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (timer.current) clearTimeout(timer.current);
        };
    }, []);

    const wait = (ms: number) => new Promise<void>(resolve => { timer.current = setTimeout(resolve, ms); });

    const run = useCallback(async (start: () => Promise<AsyncExportJobDTO>, fallbackError: string) => {
        setExporting(true);
        setProgress(0);
        try {
            let job = await start();
            while (mounted.current && (job.status === 'PENDING' || job.status === 'RUNNING')) {
                setProgress(percentOf(job));
                await wait(POLL_INTERVAL_MS);
                if (!mounted.current) return;
                job = await getAsyncExportStatus(job.jobId);
            }
            if (!mounted.current) return;
            if (job.status === 'READY') {
                setProgress(100);
                await downloadFile(await downloadAsyncExport(job.jobId), job.fileName);
            } else {
                notify.error(job.message ?? fallbackError);
            }
        } catch (error: unknown) {
            if (!mounted.current) return;
            const apiError = parseApiError(error);
            const isBusinessError = (apiError.status >= 400 && apiError.status < 500) || apiError.status === 503;
            notify.error(isBusinessError ? apiError.message : fallbackError);
        } finally {
            if (mounted.current) {
                setExporting(false);
                setProgress(null);
            }
        }
    }, [notify]);

    return { run, exporting, progress };
}
